import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { MessageSquarePlus, Users, Search, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";
import { useAuth } from "@/context/AuthContext";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import NewConversationDialog from "@/components/NewConversationDialog";
import { LoadingPanel, EmptyState } from "@/components/Bits";
import { onChange, MESSAGES_CHANGED } from "@/lib/events";

function timeAgo(iso) {
  if (!iso) return "";
  const then = new Date(iso).getTime();
  const diffMs = Date.now() - then;
  const mins = Math.floor(diffMs / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  const days = Math.floor(hrs / 24);
  if (days < 7) return `${days}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: "short", day: "numeric" });
}

function conversationLabel(c, userId) {
  const others = (c.participants || []).filter((p) => p.user_id !== userId);
  if (c.type === "group") {
    return c.title || (others.map((p) => p.name).join(", ") || "Group");
  }
  return others[0]?.name || "Conversation";
}

function initials(name) {
  return (name || "?")
    .split(" ")
    .map((p) => p[0])
    .filter(Boolean)
    .slice(0, 2)
    .join("")
    .toUpperCase();
}

export default function Dashboard() {
  const { settings } = useSettings();
  const { user } = useAuth();
  const [conversations, setConversations] = useState(null);
  const [newOpen, setNewOpen] = useState(false);

  const load = async () => {
    try {
      const { data } = await api.get("/conversations");
      setConversations(data);
    } catch {
      setConversations([]);
    }
  };

  useEffect(() => {
    load();
    const off = onChange(MESSAGES_CHANGED, load);
    return off;
  }, []);

  const loading = conversations === null;
  const rows = conversations || [];
  const unreadCount = rows.filter((c) => c.unread).length;
  const recent = rows.slice(0, 6);
  const activeToday = rows.filter(
    (c) => c.last_message_at && Date.now() - new Date(c.last_message_at).getTime() < 24 * 60 * 60 * 1000
  ).length;

  return (
    <Layout title="Overview" subtitle={settings.business_name || "Your workspace"}>
      <div className="grid gap-4 sm:grid-cols-3 mb-5">
        <div className="panel panel-pad">
          <p className="text-[11.5px] font-medium uppercase tracking-wide text-muted2">Unread</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-ink">{unreadCount}</p>
          <p className="mt-0.5 text-[12px] text-muted2">
            {unreadCount === 0 ? "You're all caught up" : `conversation${unreadCount === 1 ? "" : "s"} waiting on you`}
          </p>
        </div>
        <div className="panel panel-pad">
          <p className="text-[11.5px] font-medium uppercase tracking-wide text-muted2">Conversations</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-ink">{rows.length}</p>
          <p className="mt-0.5 text-[12px] text-muted2">across your team</p>
        </div>
        <div className="panel panel-pad">
          <p className="text-[11.5px] font-medium uppercase tracking-wide text-muted2">Active today</p>
          <p className="mt-1.5 font-display text-2xl font-semibold text-ink">{activeToday}</p>
          <p className="mt-0.5 text-[12px] text-muted2">had a message in the last 24h</p>
        </div>
      </div>

      <div className="panel">
        <div className="flex items-center justify-between px-4 sm:px-5 py-4 border-b border-line">
          <h2 className="font-display text-base font-semibold text-ink">Recent conversations</h2>
          <div className="flex items-center gap-2">
            <Button variant="ghost" size="sm" className="text-muted2" asChild>
              <Link to="/messages"><Search className="w-3.5 h-3.5 mr-1.5" />Search</Link>
            </Button>
            <Button size="sm" className="bg-brand hover:bg-brand-hover" onClick={() => setNewOpen(true)}>
              <MessageSquarePlus className="w-3.5 h-3.5 mr-1.5" />New message
            </Button>
          </div>
        </div>

        {loading ? (
          <LoadingPanel />
        ) : recent.length === 0 ? (
          <EmptyState
            icon={Users}
            title="No conversations yet"
            body="Start one with a teammate — messages and files land here."
            action={
              <Button className="bg-brand hover:bg-brand-hover" onClick={() => setNewOpen(true)}>
                <MessageSquarePlus className="w-4 h-4 mr-2" />New message
              </Button>
            }
          />
        ) : (
          <ul className="divide-y divide-line">
            {recent.map((c) => (
              <li key={c.conversation_id}>
                <Link
                  to={`/messages?c=${c.conversation_id}`}
                  className="flex items-center gap-3 px-4 sm:px-5 py-3.5 hover:bg-brand-light/40 transition-colors"
                >
                  <div className="w-9 h-9 shrink-0 rounded-full bg-brand-light text-brand flex items-center justify-center text-[12px] font-semibold">
                    {initials(conversationLabel(c, user?.user_id))}
                  </div>
                  <div className="min-w-0 flex-1">
                    <p className={`text-[13.5px] truncate ${c.unread ? "font-semibold text-ink" : "font-medium text-ink"}`}>
                      {conversationLabel(c, user?.user_id)}
                    </p>
                    <p className="text-[12px] text-muted2 truncate mt-0.5">
                      {c.last_message_preview || "No messages yet"}
                    </p>
                  </div>
                  <div className="shrink-0 flex flex-col items-end gap-1">
                    <span className="text-[11px] text-muted2">{timeAgo(c.last_message_at)}</span>
                    {c.unread && <span className="w-2 h-2 rounded-full bg-brand" />}
                  </div>
                </Link>
              </li>
            ))}
          </ul>
        )}

        {recent.length > 0 && (
          <div className="px-4 sm:px-5 py-3 border-t border-line">
            <Link to="/messages" className="inline-flex items-center gap-1 text-[12.5px] font-medium text-brand hover:text-brand-hover">
              Open Messages <ArrowRight className="w-3.5 h-3.5" />
            </Link>
          </div>
        )}
      </div>

      <NewConversationDialog open={newOpen} onOpenChange={setNewOpen} onCreated={load} />
    </Layout>
  );
}
